<template>
  <div class="form-group flex flex-col">
    <div :class="`flex flex-${align ?? 'col'} gap-2`">
      <label :for="name">{{
        props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`)
      }}</label>

      <!-- Checkbox and badge wrapper - keeps them together in a row -->
      <div class="flex flex-row items-center gap-2 h-8">
        <!-- Checkbox wrapper - shows border when proposal is accepted -->
        <div
          class="checkbox-wrapper"
          :class="[
            { 'checkbox-accepted-border': isProposalAccepted },
            `checkbox-border-${placeholderSourceForBorder}`,
          ]"
        >
          <Checkbox
            :id="name"
            v-model="internalValue"
            :disabled="disabled"
            :data-testid="`checkbox-${name}`"
            binary
          />
        </div>

        <!-- Proposal badge - shows proposed checkbox state visually -->
        <ProposalBadgeCheckbox
          v-if="hasProposal && !isProposalAccepted"
          :placeholder="normalizedPlaceholder"
          :placeholderSource="props.placeholderSource"
          :currentValue="internalValue === true"
          :disabled="disabled"
          @accept="acceptProposal"
        />
      </div>
    </div>
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';

import ProposalBadgeCheckbox from './elements/ProposalBadgeCheckbox.vue';
import FormError from './errors/FormError.vue';

const props = defineProps<{
  name: string;
  modelValue: boolean | null | undefined;
  initialValue: boolean | null | undefined;
  isDirty: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  align?: 'col' | 'row';
  isNullable?: boolean;
  placeholderSource?: InvoiceItemSuggestionSource;
  placeholder?: boolean | null; // Single boolean value from template invoice
  onAcceptPlaceholder?: () => void;
  acceptsPlaceholderData?: boolean;
}>();

const { t } = useI18n();
const { errorMessage, meta, validate, setTouched } = useField<boolean | null>(
  props.name,
  undefined,
  {
    // do not validate on mount
    validateOnMount: false,
    // do not auto-validate on every model change (we control it)
    validateOnValueUpdate: false,
  },
);
const { submitCount } = useForm();

const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const normalizedModelValue = computed<boolean | null>(() => {
  if (props.isNullable && (props.modelValue === null || props.modelValue === undefined)) {
    return null;
  }
  return props.modelValue === true;
});

const normalizedInitialValue = computed<boolean | null>(() => {
  if (props.isNullable && (props.initialValue === null || props.initialValue === undefined)) {
    return null;
  }
  return props.initialValue === true;
});

const internalValue = ref<boolean | null>(normalizedModelValue.value);
const normalizedInitialFieldValue = computed(() => normalizedInitialValue.value);

// Track if a proposal has been accepted (to show border)
const acceptedProposal = ref<boolean | null>(null);

// Computed properties
const hasProposal = computed(
  () =>
    // MUST be set: placeholderSource is required
    props.placeholderSource !== undefined &&
    // And placeholder must have a valid value
    props.placeholder !== null &&
    props.placeholder !== undefined &&
    // And it must differ from the current value
    props.placeholder !== internalValue.value,
);

const isProposalAccepted = computed(() => acceptedProposal.value !== null);

const placeholderSourceForBorder = computed(() => {
  return props.placeholderSource || 'ai';
});

// Ensure placeholder is boolean | null (not undefined)
const normalizedPlaceholder = computed((): boolean | null => {
  return props.placeholder ?? null;
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean | null): void;
}>();

watch(
  internalValue,
  (newValue, oldValue) => {
    emit('update:modelValue', newValue);

    if (newValue !== oldValue) {
      setTouched(true);
      void validate();
    }

    // When checkbox value changes, check if it's different from the proposal
    // If it is different, hide the border (user diverged from proposal)
    if (props.placeholder === null || props.placeholder === undefined) {
      // No proposal, keep border hidden
      acceptedProposal.value = null;
    } else if (newValue !== props.placeholder && acceptedProposal.value !== null) {
      // User changed value away from an accepted proposal → hide border, show badge again
      acceptedProposal.value = null;
    }
    // Otherwise keep acceptedProposal as is (don't auto-set to true)
  },
  { immediate: false }, // Don't run on initial mount - only on actual value changes
);

watch([internalValue, normalizedInitialFieldValue], () => {
  const initial = normalizedInitialFieldValue.value;
  props.isDirty(internalValue.value !== initial);
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.isNullable && (newValue === null || newValue === undefined)) {
      internalValue.value = null;
      return;
    }
    internalValue.value = newValue === true;
  },
  { flush: 'sync', immediate: false }, // Sync flush = react immediately without batching
);

// Normalize non-nullable checkbox values to false to avoid undefined/null required errors.
watch(
  () => props.modelValue,
  (newValue) => {
    if (!props.isNullable && (newValue === null || newValue === undefined)) {
      internalValue.value = false;
      emit('update:modelValue', false);
    }
  },
  { immediate: true },
);

/**
 * Accept the proposed value and apply it to the checkbox
 * Sets the acceptedProposal flag to show the border
 */
function acceptProposal() {
  if (
    props.placeholder !== null &&
    props.placeholder !== undefined &&
    props.acceptsPlaceholderData
  ) {
    // Apply the boolean value directly from props.placeholder
    internalValue.value = props.placeholder;
    acceptedProposal.value = true;
    props.onAcceptPlaceholder?.();
  }
}
</script>

<style scoped>
/* Checkbox wrapper - keeps size minimal (only around checkbox) */
.checkbox-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
}

/* Checkbox wrapper that shows border when proposal is accepted */
.checkbox-accepted-border {
  border: 2px solid;
  border-radius: 4px;
  padding: 2px;
  transition: border-color 0.2s ease;
}

/* Border colors based on proposal source */
.checkbox-accepted-border.checkbox-border-ai {
  border-color: var(--placeholder-ai-color);
}

.checkbox-accepted-border.checkbox-border-latest {
  border-color: var(--placeholder-latest-color);
}

.checkbox-accepted-border.checkbox-border-other {
  border-color: var(--placeholder-other-color);
}
</style>
